import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient, useApiRole } from '../contexts/ApiContext';
import { useEffect } from 'react';
import { mapResponse } from './helpers';
import { paths } from '../openapi/openapi';
import { useHasRole } from './access';

export function useCustomerQuery({ enabled = true } = {}) {
  const client = useApiClient();
  return useQuery({
    enabled,
    retry: true,
    throwOnError: true,
    queryKey: ['customers', 'current'],
    queryFn: ({ signal }) => client.GET('/customers/current', { signal }).then(mapResponse),
  });
}

export type CustomerSearchParams = {
  type: 'id' | 'phone' | 'username';
  value: string;
};

export function useCustomerSearchQuery(params: CustomerSearchParams | undefined) {
  const client = useApiClient();
  const query = useQuery({
    queryKey: ['customers', 'search', params?.type, params?.value],
    queryFn: ({ signal }) => {
      switch (params?.type) {
        case 'id':
          return client
            .GET('/customers/{id}', { signal, params: { path: { id: params.value } } })
            .then(mapResponse)
            .then(({ profile }) => profile);
        case 'phone':
          return client
            .GET('/customers/byPhone', { signal, params: { query: { phone: params.value } } })
            .then(mapResponse)
            .then(({ profile }) => profile);
        case 'username':
          return client
            .GET('/customers/byUsername', { signal, params: { query: { username: params.value } } })
            .then(mapResponse)
            .then(({ profile }) => profile);
        default:
          throw new Error(`Invalid search type: ${params?.type}`);
      }
    },
    enabled: Boolean(params),
    staleTime: Infinity,
  });

  const queryClient = useQueryClient();
  useEffect(() => {
    if (query.data) {
      queryClient.setQueryData(['customers', 'search', 'id', query.data.id], query.data, {
        updatedAt: query.dataUpdatedAt,
      });
      if (query.data.username) {
        queryClient.setQueryData(
          ['customers', 'search', 'username', query.data.username],
          query.data,
          { updatedAt: query.dataUpdatedAt },
        );
      }
    }
  }, [query.data, query.dataUpdatedAt, queryClient]);

  return query;
}

export function useCustomerSettingsQuery() {
  const role = useApiRole();
  const client = useApiClient();
  return useQuery({
    queryKey: ['customers', 'settings'],
    queryFn: ({ signal }) => client.GET('/settings/customer', { signal }).then(mapResponse),
    retry: true,
    refetchInterval: 60_000,
    enabled: role !== 'MEMBER',
  });
}

export function useIsCardsAvailable() {
  const role = useApiRole();
  const customerSettingsQuery = useCustomerSettingsQuery();
  const products = customerSettingsQuery.data?.products;
  const isCardsAvailable =
    role === 'MEMBER' ||
    Boolean(
      products?.physicalCardProduct?.conditions.length ||
      products?.virtualCardProduct?.conditions.length,
    );
  return isCardsAvailable;
}

export function useIsVaultsAvailable() {
  const isViewer = useHasRole('VIEWER');
  const customerSettingsQuery = useCustomerSettingsQuery();
  const isVaultsAvailable =
    isViewer ||
    Boolean(customerSettingsQuery.data?.products?.earningVaultProduct?.conditions.length);
  return isVaultsAvailable;
}

export function useIncomingTransferNetworks() {
  const query = useCustomerSettingsQuery();
  const products = query.data?.products;
  const fiatNetorks = products?.fiatIncomingExternalTransferProduct?.conditions.map(
    (it) => it.networkId,
  );
  const cryptoNetworks = products?.cryptoIncomingExternalTransferProduct?.conditions.map(
    (it) => it.transferNetwork,
  );
  return [...(fiatNetorks ?? []), ...(cryptoNetworks ?? [])];
}

export function useUpdateCustomerLegalInfoMutation({ onSuccess }: { onSuccess?: () => void } = {}) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['customers', 'current', 'legalInfo'],
    mutationFn: (
      body: paths['/customers/current/legalInfo']['put']['requestBody']['content']['application/json'],
    ) => client.PUT('/customers/current/legalInfo', { body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['customers', 'current'] });
      onSuccess?.();
    },
  });
}
